import _ from 'lodash';
import { toJS } from 'mobx';
import LogRocket from 'shared/logrocket';
import { identifyLDUser } from 'shared/observability/ld-identify';
import uuid from 'uuid';
import { setPersistedValue, removePersistedValue } from 'shared/utils/persisted-values';

import { consumerSignupV2 } from 'src/state/actions/users';
import { addCustomerToOrder } from 'shared/graphql/order/mutations';
import { isWithinThreshold } from 'shared/helpers/orders';

// called via src/checkout/components/confirmation/submit 29
export default async function afterSubmit({
  serverOrder,
  showContactSupportModal,
  showEmptyCartModal,
  Cart,
  UI,
  Modals,
  User,
  apolloClient,
  showErnie,
  CheckoutCtx,
  mobileEcommHandler,
  serverOrderThreshold,
}) {
  const {
    cannabisTax,
    creditTotal,
    customer,
    deliveryFee,
    dispensary,
    isAfterHoursOrder,
    isGuestOrder,
    messages,
    otherFees,
    orders,
    subtotal,
    taxAmount,
    tipAmount,
    totalCost,
  } = serverOrder;

  const { returnUrl, variant: checkoutVariant, isVariant } = CheckoutCtx;
  const { order: clientOrder } = Cart;
  const { isMobileEcommApp } = UI;
  const isDutchiePlus = checkoutVariant === 'isolatedCheckout';

  Cart.analyticsCartPurchase(serverOrder.orders, serverOrder, serverOrder.dispensary);

  if (!isGuestOrder && (User.id !== customer._id || clientOrder.dispensary.id !== dispensary._id)) {
    showContactSupportModal();
    return;
  }

  const mappedServerOrderProducts = _.map(orders, ({ basePrice, price, product, quantity }) => ({
    basePrice,
    id: product._id,
    price,
    quantity,
  }));

  const serverCostBreakdown = {
    cannabisTax,
    creditTotal,
    deliveryFee,
    otherFees,
    orders: mappedServerOrderProducts,
    subtotal,
    taxAmount,
    tipAmount,
    totalCost,
  };

  const { costBreakdown: clientCostBreakdown, openInfo } = Cart;

  clientCostBreakdown.orders = _.map(_.values(clientOrder.cart), ({ basePrice, price, product, quantity }) => ({
    basePrice,
    id: product._id,
    price,
    quantity,
  }));

  if (messages) {
    _.forEach(messages, (message) => {
      showErnie(message, `danger`);
      console.log(`order adjustment message: `, message);
    });
  }

  const totalCostArgs = {
    actual: clientCostBreakdown?.total ?? 0,
    expected: totalCost,
    threshold: serverOrderThreshold,
  };

  const serverOrderMismatch = !isWithinThreshold(totalCostArgs);

  if (serverOrderMismatch) {
    console.error('Server Order Data Mismatch.', clientCostBreakdown, serverCostBreakdown);

    if (_.size(clientOrder.cart) > _.size(serverOrder.orders)) {
      if (_.size(serverOrder.orders) === 0) {
        showErnie('An item in your cart has sold out and been removed.', `danger`);
        // FIXME: I think this is broken
        showEmptyCartModal();
        return;
      }
      _.forEach(clientOrder.cart, (clientItem, key) => {
        if (!_.find(serverOrder.orders, [`product._id`, clientItem.product._id])) {
          Cart.removeItem(key);
        }
      });
      showErnie('An item in your cart has sold out and been removed.', `danger`);
      return;
    }

    if (clientOrder.coupon?._id !== serverOrder.coupon?._id) {
      Cart.removeCoupon();
      return;
    }

    showContactSupportModal();
    return;
  }

  // build guest profile for account creation before cart is cleared
  if (Cart.order.isGuestOrder) {
    Cart.guestCouponError = false;
    const { email, password, firstName, lastName, phone, birthDay, birthMonth, birthYear } = toJS(
      Cart.order.guestCustomer
    );

    const hasBirthdate = birthMonth && birthDay && birthYear;
    const emailOptIn = clientOrder.dispensary.storeSettings?.subscriptions?.enabled
      ? _.get(Cart.order, `guestCustomer.emailOptIn`, false)
      : true;

    Cart.guestInfoToSave.customer = {
      email,
      password,
      profile: _.omitBy(
        {
          firstName,
          lastName,
          phone,
          birthday: hasBirthdate ? `${birthMonth}/${birthDay}/${birthYear}` : null,
          textNotifications: true,
          emailNotifications: true,
          emailOptIn,
        },
        _.isNil
      ),
    };

    if (serverOrder.medicalOrder && serverOrder.medicalCard) {
      const { number, expirationDate, state, photo } = serverOrder.medicalCard;
      Cart.guestInfoToSave.customer.profile.medicalCard = {
        number,
        expirationDate,
        state,
        photo,
      };
    }
    if (Cart.order.guestCustomer?.photoId && Cart.guestInfoToSave) {
      Cart.guestInfoToSave.customer.profile.photoId = Cart.order.guestCustomer.photoId;
    }

    Cart.stashedOrder = toJS({
      ...clientOrder,
      id: serverOrder.id,
      orderId: serverOrder.orderId,
      updateOrderToken: serverOrder.updateOrderToken,
      isAfterHoursOrder,
      isInStorePickup: Cart.isInStorePickup,
      isCurbsidePickup: Cart.isCurbsidePickup,
      isDriveThruPickup: Cart.isDriveThruPickup,
      isDelivery: Cart.isDelivery,
      openInfo,
      guestInfoToSave: Cart.guestInfoToSave,
    });
  }

  // we need to reset some persisted fields after the order has gone through successfully.
  const saveGuestInfo = toJS(Cart.saveGuestInfo);
  const performGuestInfoSave = saveGuestInfo && !serverOrder?.customer;
  const { guestInfoToSave } = Cart;
  Cart.saveGuestInfo = false;

  // after the order is complete we want to remove the utm session data
  removePersistedValue('utmSessionData', true);

  if (!isDutchiePlus) {
    Cart.clearOrder();
  }

  if (isMobileEcommApp) {
    mobileEcommHandler();
  }

  Cart.hypurAccessToken = null;

  try {
    /* eslint-disable */
    LogRocket?.track('Checkout');
    LogRocket?.track(`Order:${serverOrder.orderId}`);
    /* eslint-enable */

    if (serverOrder.isGuestOrder && LogRocket) {
      const guestKey = uuid.v4();
      const guestTraits = {
        name: `${serverOrder.guestCustomer?.firstName} ${serverOrder.guestCustomer?.lastName}`,
        email: serverOrder.guestCustomer?.email,
        orderId: serverOrder.orderId,
        phone: serverOrder.guestCustomer?.phone,
      };
      LogRocket.identify(guestKey, guestTraits);
      identifyLDUser(guestKey, guestTraits);
    } else if (User.id && LogRocket) {
      const userTraits = {
        name: `${User.profile.firstName} ${User.profile.lastName}`,
        email: User.email,
        orderId: serverOrder.orderId,
        phone: User.profile.phone,
      };
      LogRocket.identify(User.id, userTraits);
      identifyLDUser(User.id, userTraits);
    }
  } catch (e) {
    console.log(`Couldn't set logrocket info.`);
    console.error(e);
  }

  let plusDefaultReturnUrl;

  if (isDutchiePlus) {
    plusDefaultReturnUrl = clientOrder.dispensary.plusSettings?.defaultReturnUrl;

    if (!plusDefaultReturnUrl && !returnUrl) {
      console.error('No return url found');
      return;
    }

    if (returnUrl) {
      plusDefaultReturnUrl = returnUrl;
    }
  }

  // TODO convert checkoutVariant to enum;
  // checkoutVariant can also be 'isolatedCheckout' or 'embedded' or 'mobile-ecomm'
  const isNonStandardCheckout = checkoutVariant !== 'default' || serverOrder.isGuestOrder;

  if (isNonStandardCheckout) {
    User.viewOrderId = serverOrder.orderId;
    setPersistedValue('viewOrderId', serverOrder.orderId, true);
    // Store the order data for guest checkout
    if (serverOrder.isGuestOrder) {
      setPersistedValue('stashedOrder', JSON.stringify(Cart.stashedOrder), true);
    }
  }

  const { cName } = serverOrder.dispensary;
  if (isNonStandardCheckout && !performGuestInfoSave) {
    if (isDutchiePlus) {
      plusRedirectAfterCheckout(plusDefaultReturnUrl, serverOrder);
      return;
    }
    await redirectAfterCheckout(UI, cName, isMobileEcommApp);

    if (serverOrder.isGuestOrder) {
      UI.viewingGuestOrderSuccess = true;
    } else {
      // FIXME: we should use the ModalNames type. was not able to import from shared
      Modals.openModal(`orderStatus`);
    }
  } else if (isNonStandardCheckout && performGuestInfoSave) {
    // create an account for guest customer
    const userCreationData = {
      dispensaryId: serverOrder.dispensary._id,
      embedded: isVariant([`embedded`, `store-front`]),
    };
    // this isn't a hook so we can't use the hook
    const signupResult = await consumerSignupV2({
      apolloClient,
      User,
      Cart,
      UI,
      profile: guestInfoToSave.customer,
      userCreationData,
    });
    if (signupResult.success) {
      // update order obj
      const updateOrder = await apolloClient.mutate({
        mutation: addCustomerToOrder,
        variables: {
          input: { orderId: serverOrder.id },
        },
      });
      if (updateOrder.data.addCustomerToOrder.updated) {
        try {
          showErnie('Success! Your account has been created and your order has been placed.', `success`);
          // send to orders page
          if (isVariant(`default`)) {
            UI.showOrder(Cart.stashedOrder.orderId);
          } else {
            if (isDutchiePlus) {
              setTimeout(() => plusRedirectAfterCheckout(plusDefaultReturnUrl, serverOrder), 3000);
              return;
            }
            await redirectAfterCheckout(UI, cName, isMobileEcommApp);
            // FIXME: we should use the ModalNames type. was not able to import from shared
            Modals.openModal(`orderStatus`);
          }
        } catch (e) {
          console.log(e);
          showErnie('There was an error logging you in. Please contact support', `danger`);
          if (isDutchiePlus) {
            setTimeout(() => plusRedirectAfterCheckout(plusDefaultReturnUrl, serverOrder), 3000);
            return;
          }
          await redirectAfterCheckout(UI, cName, isMobileEcommApp, true, Cart);
        }
      } else {
        showErnie('There was an error adding this order to your new account. Please contact support', `danger`);
        if (isDutchiePlus) {
          setTimeout(() => plusRedirectAfterCheckout(plusDefaultReturnUrl, serverOrder), 3000);
          return;
        }
        await redirectAfterCheckout(UI, cName, isMobileEcommApp, true, Cart);
      }
    } else {
      console.error(signupResult);
      showErnie('There was an error creating your account. Please contact support', `danger`);
    }
  } else {
    UI.showOrder(serverOrder.orderId);
  }
}

async function redirectAfterCheckout(UI, cName, isMobileEcommApp, error, Cart) {
  if (isMobileEcommApp && error) {
    Cart.clearOrder();
  } else {
    await UI.showMenu(cName);
  }
}

async function plusRedirectAfterCheckout(plusDefaultReturnUrl, serverOrder) {
  window.location.href = `${plusDefaultReturnUrl}?orderNumber=${serverOrder.orderId}`;
}
